import { Box, Skeleton, Typography } from '@compass/core';
import { PieChart, Export, Series, Label, Connector, Legend, Tooltip, Title, Font } from 'devextreme-react/pie-chart';
import { useParticipantsData } from '../contexts/ParticipantsDataContext';
import { useDashboardChartView } from '../contexts/DashboardChartViewContext';
import { DashboardCard, DashboardCardContext } from '../components/DashboardCard';
import { ParticipantsFilterControls } from './ParticipantsFilterControls';
import { ParticipantStatusChartValue } from '../types/ParticipantStatusData';
import { useRef, useMemo, useContext, useState } from 'react';
import Chart, { Label as CartesianLabel, Legend as CartesianLegend, Series as CartesianSeries, Tooltip as CartesianTooltip, ValueAxis } from 'devextreme-react/chart';
import { DashboardChartViewSelect } from '../components/DashboardChartViewSelect';
import { DASHBOARD_CHART_VIEW_CONFIG, DASHBOARD_DISTRIBUTION_COLORS, DashboardChartView, getDashboardDistributionColor } from '../types';

interface ChartLabelArg {
    argumentText: string;
    valueText: string;
    value: number;
    percent: number;
    point?: {
        data?: ParticipantStatusChartValue;
    };
}

const ParticipantsChartContent = ({ chartRef, cartesianChartRef, chartView, setChartView }: { chartRef: React.RefObject<PieChart>; cartesianChartRef: React.RefObject<Chart>; chartView: DashboardChartView; setChartView: (view: DashboardChartView) => void }) => {
    const { chartData, loading, error, data, filter, programs } = useParticipantsData();
    const { isExpanded } = useContext(DashboardCardContext);

    const styleConfig = isExpanded ? {
        centerTitleSize: 42,
        centerSubSize: 18,
        innerRadius: 0.68,
        labelSize: 14
    } : {
        centerTitleSize: 32,
        centerSubSize: 14,
        innerRadius: 0.65,
        labelSize: 12
    };

    // Prepare metadata for export
    const exportMetadata = useMemo(() => {
        const yearLabel = filter.schoolYear ? `${filter.schoolYear}-${filter.schoolYear + 1}` : 'All Years';
        const programLabel = filter.programId 
            ? programs.find(p => p.id === filter.programId)?.name || 'Unknown Program'
            : 'All Programs';
        
        return {
            title: 'Participants Assisted',
            subtitle: `Year: ${yearLabel} | Program: ${programLabel}`
        };
    }, [filter.schoolYear, filter.programId, programs]);

    const totalParticipants = data?.totalParticipants || 0;

    if (loading) {
        return <Skeleton variant="rounded" height={400} />;
    }

    if (error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!chartData.length) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <Typography variant="body2" color="text.secondary">
                    No participants found for selected filters
                </Typography>
            </Box>
        );
    }

    const formatPercentage = (percent: number): string => {
        const pct = percent;
        if (pct > 0 && pct < 1) return '<1';
        return Math.round(pct).toString();
    };

    const centerTemplate = () => {
        return (
            <svg>
                <text 
                    textAnchor="middle" 
                    x="50%" 
                    y="45%" 
                    style={{ 
                        fontSize: `${styleConfig.centerTitleSize}px`, 
                        fontWeight: 'bold',
                        fill: '#000'
                    }}
                >
                    {totalParticipants.toLocaleString()}
                </text>
                <text 
                    textAnchor="middle" 
                    x="50%" 
                    y="55%" 
                    style={{ 
                        fontSize: `${styleConfig.centerSubSize}px`,
                        fill: '#666'
                    }}
                >
                    Total Participants
                </text>
            </svg>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}><ParticipantsFilterControls /><DashboardChartViewSelect dashboard="participants" value={chartView} onChange={setChartView} /></Box>
            
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                {(chartView === 'pie' || chartView === 'fullPie') ? <PieChart
                    ref={chartRef}
                    id="participants-pie-chart"
                    type={chartView === 'fullPie' ? 'pie' : 'doughnut'}
                    dataSource={chartData}
                    innerRadius={styleConfig.innerRadius}
                    palette={DASHBOARD_DISTRIBUTION_COLORS}
                    centerRender={centerTemplate}
                    width="100%"
                    height="100%"
                    resolveLabelOverlapping="shift"
                >
                    <Title text={exportMetadata.title} subtitle={exportMetadata.subtitle}>
                        <Font size={18} weight={600} />
                    </Title>
                    <Series argumentField="label" valueField="value">
                        <Label 
                            visible={true}
                            position="outside"
                            customizeText={(arg: ChartLabelArg) => {
                                const count = arg.point?.data?.count ?? 0;
                                const displayPct = formatPercentage(arg.value);
                                return `${arg.argumentText}\n${count} (${displayPct}%)`;
                            }}
                        >
                            <Font size={styleConfig.labelSize} />
                            <Connector visible={true} width={1} />
                        </Label>
                    </Series>
                    <Legend visible={false} />
                    <Tooltip
                        enabled={true}
                        customizeTooltip={(arg: ChartLabelArg) => {
                            const count = arg.point?.data?.count ?? 0;
                            const displayPct = formatPercentage(arg.value);
                            return {
                                text: `${arg.argumentText}: ${count} (${displayPct}%)`
                            };
                        }}
                    />
                    <Export enabled={isExpanded} />
                </PieChart> : <Chart ref={cartesianChartRef} dataSource={chartData} height="100%" rotated={chartView === 'horizontalBar'} palette={DASHBOARD_DISTRIBUTION_COLORS} customizePoint={(args: any) => ({ color: getDashboardDistributionColor(args.index) })}>
                    <CartesianSeries type="bar" argumentField="label" valueField="count"><CartesianLabel visible /></CartesianSeries>
                    <ValueAxis allowDecimals={false} /><CartesianLegend visible={false} /><CartesianTooltip enabled />
                </Chart>}
            </Box>
        </Box>
    );
};

export const ParticipantsChartCard = () => {
    const { filter, programs } = useParticipantsData();
    const chartRef = useRef<PieChart>(null);
    const cartesianChartRef = useRef<Chart>(null);
    const [chartView, setChartView] = useDashboardChartView('participants');

    const handleExport = () => {
        if ((chartView === 'pie' || chartView === 'fullPie') && chartRef.current) {
            const yearLabel = filter.schoolYear || 'AllYears';
            const programName = filter.programId 
                ? programs.find(p => p.id === filter.programId)?.name?.replace(/\s+/g, '_') || 'Program'
                : 'AllPrograms';
            chartRef.current.instance.exportTo(`Participants_${yearLabel}_${programName}`, 'png');
        } else cartesianChartRef.current?.instance.exportTo(`Participants_${filter.schoolYear || 'AllYears'}`, 'png');
    };

    return (
        <DashboardCard title="Participants" onExport={handleExport}>
            <ParticipantsChartContent chartRef={chartRef} cartesianChartRef={cartesianChartRef} chartView={chartView} setChartView={setChartView} />
        </DashboardCard>
    );
};
